import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { organigrammeAPI } from "../services/api";
import { useAuth } from "../hooks/useAuth";

const DepartementCountContext = createContext();

export function DepartementCountProvider({ children }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [departementCount, setDepartementCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour charger les statistiques des départements
  const loadDepartementStats = useCallback(async () => {
    // Vérifier si l'utilisateur est authentifié
    if (!isAuthenticated) {
      setDepartementCount(0);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);


      // Essayer d'abord l'endpoint stats spécialisé
      const response = await organigrammeAPI.getOrganizationChart();

      const stats = response.statistics;
      const count = stats.total_departments ?? stats.totalDepartements ?? 0;
      setDepartementCount(count);
      return; // Succès, pas besoin de fallback
    } catch (statsError) {
      console.warn("⚠️ Endpoint stats non disponible, fallback vers organigramme complet:", statsError.message);

      try {
        // Fallback vers l'organigramme complet
        const getOrganizationChartResponse = await organigrammeAPI.getOrganizationChart();

        const stats = getOrganizationChartResponse?.statistics;
        const count = stats.total_departments ?? stats.totalDepartements ?? 0;
        setDepartementCount(count);
      } catch (fallbackError) {
        console.error("❌ Erreur lors du chargement des statistiques départements:", fallbackError);
        setError(fallbackError.message || "Erreur de chargement");
        setDepartementCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fonction pour rafraîchir les statistiques (utilisable par les autres composants)
  const refreshDepartementStats = useCallback(async () => {
    await loadDepartementStats();
  }, [loadDepartementStats]);

  // Charger les statistiques au montage du contexte et lors des changements d'authentification
  useEffect(() => {
    // Ne pas charger tant que l'authentification n'est pas initialisée
    if (authLoading) {
      return;
    }


    if (isAuthenticated) {
      loadDepartementStats();
    } else {
      // Réinitialiser les données si l'utilisateur n'est pas connecté
      setDepartementCount(0);
      setError(null);
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, loadDepartementStats]);

  const contextValue = {
    departementCount,
    loading,
    error,
    setDepartementCount,
    refreshDepartementStats,
  };

  return <DepartementCountContext.Provider value={contextValue}>{children}</DepartementCountContext.Provider>;
}

export function useDepartementCount() {
  const context = useContext(DepartementCountContext);
  if (!context) {
    throw new Error("useDepartementCount must be used within a DepartementCountProvider");
  }
  return context;
}
